/**
 * Copyright 2024-2026, Optimizely
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 * http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { __awaiter } from "tslib";
import { getBatchedAsync, getBatchedSync } from "../utils/cache/store";
import { buildLogEvent } from "./event_builder/log_event";
import { ExponentialBackoff } from "../utils/repeater/repeater";
import { BaseService, ServiceState } from "../service";
import { runWithRetry } from "../utils/executor/backoff_retry_runner";
import { isSuccessStatusCode } from "../utils/http_request_handler/http_util";
import { EventEmitter } from "../utils/event_emitter/event_emitter";
import { IdGenerator } from "../utils/id_generator";
import { areEventContextsEqual } from "./event_builder/user_event";
import { FAILED_TO_DISPATCH_EVENTS, SERVICE_NOT_RUNNING } from "error_message";
import { OptimizelyError } from "../error/optimizly_error";
import { sprintf } from "../utils/fns";
import { SERVICE_STOPPED_BEFORE_RUNNING } from "../service";
export const DEFAULT_MIN_BACKOFF = 200;
export const DEFAULT_MAX_BACKOFF = 1000;
export const MAX_EVENTS_IN_STORE = 500;
export const LOGGER_NAME = 'BatchEventProcessor';
export class BatchEventProcessor extends BaseService {
    constructor(config) {
        var _a;
        super(config.startupLogs);
        this.eventQueue = [];
        this.idGenerator = new IdGenerator();
        this.runningTask = new Map();
        this.dispatchingEvents = new Map();
        this.eventEmitter = new EventEmitter();
        this.eventDispatcher = config.eventDispatcher;
        this.closingEventDispatcher = config.closingEventDispatcher;
        this.batchSize = config.batchSize;
        this.eventStore = config.eventStore;
        this.retryConfig = config.retryConfig;
        this.dispatchRepeater = config.dispatchRepeater;
        this.dispatchRepeater.setTask(() => this.flush());
        this.failedEventRepeater = config.failedEventRepeater;
        (_a = this.failedEventRepeater) === null || _a === void 0 ? void 0 : _a.setTask(() => this.retryFailedEvents());
        if (config.logger) {
            this.setLogger(config.logger);
        }
    }
    setLogger(logger) {
        this.logger = logger;
        this.logger.setName(LOGGER_NAME);
    }
    onDispatch(handler) {
        return this.eventEmitter.on('dispatch', handler);
    }
    retryFailedEvents() {
        return __awaiter(this, void 0, void 0, function* () {
            if (!this.eventStore) {
                return;
            }
            const keys = (yield this.eventStore.getKeys()).filter((k) => !this.dispatchingEvents.has(k) && !this.eventQueue.find((e) => e.id === k));
            const events = yield (this.eventStore.operation === 'sync' ?
                getBatchedSync(this.eventStore, keys) : getBatchedAsync(this.eventStore, keys));
            const failedEvents = [];
            events.forEach((e) => {
                if (e) {
                    failedEvents.push(e);
                }
            });
            if (failedEvents.length == 0) {
                return;
            }
            failedEvents.sort((a, b) => a.id < b.id ? -1 : 1);
            const batches = [];
            let currentBatch = [];
            failedEvents.forEach((event) => {
                if (currentBatch.length === this.batchSize ||
                    (currentBatch.length > 0 && !areEventContextsEqual(currentBatch[0].event, event.event))) {
                    batches.push({
                        request: buildLogEvent(currentBatch.map((e) => e.event)),
                        events: currentBatch,
                    });
                    currentBatch = [];
                }
                currentBatch.push(event);
            });
            if (currentBatch.length > 0) {
                batches.push({
                    request: buildLogEvent(currentBatch.map((e) => e.event)),
                    events: currentBatch,
                });
            }
            batches.forEach((batch) => {
                this.dispatchBatch(batch, false);
            });
        });
    }
    createNewBatch() {
        if (this.eventQueue.length == 0) {
            return;
        }
        const events = [];
        const eventWithIds = [];
        this.eventQueue.forEach((event) => {
            events.push(event.event);
            eventWithIds.push(event);
        });
        this.eventQueue = [];
        return { request: buildLogEvent(events), events: eventWithIds };
    }
    executeDispatch(request, closing = false) {
        return __awaiter(this, void 0, void 0, function* () {
            const dispatcher = closing && this.closingEventDispatcher ? this.closingEventDispatcher : this.eventDispatcher;
            return dispatcher.dispatchEvent(request).then((res) => {
                if (res.statusCode && !isSuccessStatusCode(res.statusCode)) {
                    return Promise.reject(new OptimizelyError(FAILED_TO_DISPATCH_EVENTS, res.statusCode));
                }
                return Promise.resolve(res);
            });
        });
    }
    dispatchBatch(batch, closing) {
        const { request, events } = batch;
        events.forEach((event) => {
            this.dispatchingEvents.set(event.id, event);
        });
        const runResult = this.retryConfig
            ? runWithRetry(() => this.executeDispatch(request, closing), this.retryConfig.backoffProvider(), this.retryConfig.maxRetries) : {
            result: this.executeDispatch(request, closing),
            cancelRetry: () => { },
        };
        this.eventEmitter.emit('dispatch', request);
        const taskId = this.idGenerator.getId();
        this.runningTask.set(taskId, runResult);
        runResult.result.then((res) => {
            events.forEach((event) => {
                var _a;
                (_a = this.eventStore) === null || _a === void 0 ? void 0 : _a.remove(event.id);
            });
            return Promise.resolve();
        }).catch((err) => {
            var _a;
            // if the dispatch fails, the events will still be
            // in the store for future processing
            (_a = this.logger) === null || _a === void 0 ? void 0 : _a.error(err);
        }).finally(() => {
            this.runningTask.delete(taskId);
            events.forEach((event) => this.dispatchingEvents.delete(event.id));
        });
    }
    flush(useClosingDispatcher = false) {
        return __awaiter(this, void 0, void 0, function* () {
            const batch = this.createNewBatch();
            if (!batch) {
                return;
            }
            this.dispatchRepeater.reset();
            this.dispatchBatch(batch, useClosingDispatcher);
        });
    }
    process(event) {
        var _a;
        return __awaiter(this, void 0, void 0, function* () {
            if (!this.isRunning()) {
                return Promise.reject(new OptimizelyError(SERVICE_NOT_RUNNING, 'BatchEventProcessor'));
            }
            const eventWithId = {
                id: this.idGenerator.getId(),
                event: event,
            };
            // store in eventStore first to avoid data loss
            yield Promise.resolve((_a = this.eventStore) === null || _a === void 0 ? void 0 : _a.set(eventWithId.id, eventWithId)).catch((e) => {
                var _a;
                (_a = this.logger) === null || _a === void 0 ? void 0 : _a.error(e);
            });
            if (this.eventQueue.length > 0 && !areEventContextsEqual(this.eventQueue[0].event, event)) {
                this.flush();
            }
            this.eventQueue.push(eventWithId);
            if (this.eventQueue.length == this.batchSize) {
                this.flush();
            }
            else if (!this.dispatchRepeater.isRunning()) {
                this.dispatchRepeater.start();
            }
        });
    }
    start() {
        var _a;
        if (!this.isNew()) {
            return;
        }
        super.start();
        this.state = ServiceState.Running;
        if (!this.disposable) {
            (_a = this.failedEventRepeater) === null || _a === void 0 ? void 0 : _a.start();
        }
        this.retryFailedEvents();
        this.startPromise.resolve();
    }
    makeDisposable() {
        var _a, _b, _c;
        super.makeDisposable();
        this.batchSize = 1;
        this.retryConfig = {
            maxRetries: Math.min((_b = (_a = this.retryConfig) === null || _a === void 0 ? void 0 : _a.maxRetries) !== null && _b !== void 0 ? _b : 5, 5),
            backoffProvider: ((_c = this.retryConfig) === null || _c === void 0 ? void 0 : _c.backoffProvider) ||
                (() => new ExponentialBackoff(DEFAULT_MIN_BACKOFF, DEFAULT_MAX_BACKOFF, 500)),
        };
    }
    flushImmediately() {
        if (!this.isRunning()) {
            return Promise.resolve();
        }
        return this.flush(true);
    }
    stop() {
        var _a;
        if (this.isDone()) {
            return;
        }
        if (this.isNew()) {
            this.startPromise.reject(new Error(sprintf(SERVICE_STOPPED_BEFORE_RUNNING, 'BatchEventProcessor')));
        }
        this.state = ServiceState.Stopping;
        this.dispatchRepeater.stop();
        (_a = this.failedEventRepeater) === null || _a === void 0 ? void 0 : _a.stop();
        this.flush(true);
        this.runningTask.forEach((task) => task.cancelRetry());
        Promise.allSettled(Array.from(this.runningTask.values()).map((task) => task.result)).then(() => {
            this.state = ServiceState.Terminated;
            this.stopPromise.resolve();
        });
    }
}
export const __platforms = ['__universal__'];
//# sourceMappingURL=batch_event_processor.js.map